/**
 * H5 加载性能：冷启（Navigation Timing 全量）与 SPA 切页（关键节点）
 */

import { hasSessionReadyCookie } from '@/utils/fetch/session-ready-cookie';

import { getDocumentDclDurationMs, getNavElapsedMs } from '../loading-timing';

import { collectColdNavigationPerfSnapshot, getNavigationToJsDuration } from './collect-navigation';
import { logH5ColdLoadPerfTrack, logH5SpaRoutePerfTrack } from './h5-load-perf-event';
import { measureNavOriginRafPaintMs } from './raf-paint';
import {
    markRouteVersionPerfReported,
    normalizePerfPathname,
    peekIsRouteVersionFirstLoad,
} from './route-version-first';

let hasReportedColdLoadPerf = false;
let spaPerfGeneration = 0;

/**
 * 文档冷启：Navigation/Paint Timing + 双 rAF 参考耗时（每文档一次）
 *
 * @description fp/fcp 常缺失时，以 dcl_duration（Navigation）与 raf_paint_duration 作参考
 */
export function reportH5ColdLoadPerf(entryPathname: string): void {
    if (typeof window === 'undefined' || hasReportedColdLoadPerf) {
        return;
    }
    hasReportedColdLoadPerf = true;

    // 冷启首屏 pathname 规范化（末尾 `/` 去掉；根路径保留 `/）
    const entryRouteKey = normalizePerfPathname(entryPathname);
    // 对应 pathname 是否尚未完成过一次 H5 路由性能上报，这里如果是 1 则表示是首次加载，0 则表示不是首次加载（含缓存情况）
    const isRouteVersionFirstLoad = peekIsRouteVersionFirstLoad(entryRouteKey) ? 1 : 0;

    const timing = collectColdNavigationPerfSnapshot();
    if (timing.dcl_duration == null) {
        const dclFallback = getDocumentDclDurationMs();
        if (dclFallback != null) {
            timing.dcl_duration = dclFallback;
        }
    }

    const baseParams = {
        entry_pathname: entryPathname,
        is_cold_start: 1 as const,
        is_route_version_first_load: isRouteVersionFirstLoad,
        had_session_cookie: hasSessionReadyCookie() ? 1 : 0,
        timing,
        navigation_to_js_duration: getNavigationToJsDuration(),
    };

    measureNavOriginRafPaintMs(rafPaintMs => {
        logH5ColdLoadPerfTrack({
            ...baseParams,
            raf_paint_duration: rafPaintMs,
        });
        markRouteVersionPerfReported(entryRouteKey);
    });
}

export interface IScheduleH5SpaRoutePerfParams {
    toPathname: string;
    fromPathname: string;
}

/**
 * SPA 切页：layout 阶段记起点，双 rAF 后上报 route_to_paint_duration
 */
export function scheduleH5SpaRoutePerf(params: IScheduleH5SpaRoutePerfParams): void {
    if (typeof window === 'undefined') {
        return;
    }

    const generation = ++spaPerfGeneration;
    const routeStartMs = getNavElapsedMs();
    const { toPathname, fromPathname } = params;
    const toRouteKey = normalizePerfPathname(toPathname);
    const isRouteVersionFirstLoad = peekIsRouteVersionFirstLoad(toRouteKey) ? 1 : 0;

    requestAnimationFrame(() => {
        requestAnimationFrame(() => {
            if (generation !== spaPerfGeneration) {
                return;
            }
            logH5SpaRoutePerfTrack({
                // 目标路由（usePathname 原始值，上报字段不做尾斜杠规范化）
                to_pathname: toPathname,
                // 上一屏路由（GlobalTrackerSync 内 syncTrackerRoute 前的快照）
                from_pathname: fromPathname,
                // pathname 变更触发的 layout 时刻 → 双 rAF（ms），近似 SPA 首帧
                route_to_paint_duration: Math.round(getNavElapsedMs() - routeStartMs),
                // 同 HTML 文档内非整页冷启入口，SPA 固定 0
                is_cold_start: 0,
                // 当前 app_version 下 to_pathname 是否首次完成路由性能采样（与 is_cold_start 无关）
                is_route_version_first_load: isRouteVersionFirstLoad,
            });
            markRouteVersionPerfReported(toRouteKey);
        });
    });
}
